import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { LogLevel, LogEntry, LoggerConfig } from '../interfaces/logger.interface';
import { LOGGER_CONFIG } from '../config/logger.tokens';

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private sessionId: string;
  private logBuffer: LogEntry[] = [];
  private flushTimer: any;
  private retryQueue: LogEntry[] = [];

  constructor(
    @Inject(LOGGER_CONFIG) private config: LoggerConfig,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    this.sessionId = this.generateSessionId();
    this.initializeBuffering();
  }

  debug(message: string, context?: any, source?: string): void {
    this.log(LogLevel.DEBUG, message, context, source);
  }

  info(message: string, context?: any, source?: string): void {
    this.log(LogLevel.INFO, message, context, source);
  }

  warn(message: string, context?: any, source?: string): void {
    this.log(LogLevel.WARN, message, context, source);
  }

  error(message: string, error?: Error, context?: any, source?: string): void {
    const errorContext = error ? {
      name: error.name,
      message: error.message,
      stack: this.config.enableStackTrace ? error.stack : undefined,
      ...context
    } : context;

    this.log(LogLevel.ERROR, message, errorContext, source, error?.stack);
  }

  critical(message: string, error?: Error, context?: any, source?: string): void {
    const errorContext = error ? {
      name: error.name,
      message: error.message,
      stack: this.config.enableStackTrace ? error.stack : undefined,
      ...context
    } : context;

    this.log(LogLevel.CRITICAL, message, errorContext, source, error?.stack);
    // Force immediate flush for critical errors
    this.flush();
  }

  private log(level: LogLevel, message: string, context?: any, source?: string, errorStack?: string): void {
    if (level < this.config.minLevel) {
      return;
    }

    const platform = isPlatformBrowser(this.platformId) ? 'browser' : 'server';
    const platformBadge = platform === 'server' ? 'SSR' : 'CSR';

    // If no source provided, use platform as source for better identification
    const finalSource = source || `[${platformBadge}]`;

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context: this.sanitizeContext(context),
      source: finalSource,
      sessionId: this.sessionId,
      platform,
      errorStack,
      ...this.getPlatformSpecificData()
    };

    // If bufferSize is 1, log immediately without buffering (avoid double logging)
    // Otherwise, use buffering for batch processing
    if (this.config.bufferSize <= 1) {
      this.processEntry(entry);
    } else {
      this.addToBuffer(entry);
    }
  }

  private processEntry(entry: LogEntry): void {
    this.config.transports.forEach(transport => {
      if (entry.level >= transport.minLevel) {
        try {
          const result = transport.log(entry);
          if (result instanceof Promise) {
            result.catch(() => {
              this.addToRetryQueue(entry);
            });
          }
        } catch (error) {
          this.addToRetryQueue(entry);
        }
      }
    });
  }

  private sanitizeContext(context: any): any {
    if (!context) return context;

    const sensitiveFields = ['password', 'token', 'authorization', 'secret', 'key', 'creditCard', 'ssn'];
    const sanitized = JSON.parse(JSON.stringify(context));

    const sanitizeObject = (obj: any): any => {
      if (typeof obj !== 'object' || obj === null) return obj;

      for (const key in obj) {
        const lowerKey = key.toLowerCase();
        if (sensitiveFields.some(field => lowerKey.includes(field))) {
          obj[key] = '[REDACTED]';
        } else if (typeof obj[key] === 'object') {
          obj[key] = sanitizeObject(obj[key]);
        }
      }
      return obj;
    };

    return sanitizeObject(sanitized);
  }

  private getPlatformSpecificData(): Partial<LogEntry> {
    if (isPlatformBrowser(this.platformId)) {
      return {
        url: window.location.href,
        userAgent: navigator.userAgent
      };
    } else if (isPlatformServer(this.platformId)) {
      return {
        // Server-specific data can be added here
        // url and userAgent would come from request context
      };
    }
    return {};
  }

  private generateSessionId(): string {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private addToBuffer(entry: LogEntry): void {
    this.logBuffer.push(entry);
    if (this.logBuffer.length >= this.config.bufferSize) {
      this.flush();
    }
  }

  private initializeBuffering(): void {
    if (this.config.flushInterval > 0) {
      this.flushTimer = setInterval(() => {
        this.flush();
      }, this.config.flushInterval);
    }
  }

  private flush(): void {
    if (this.logBuffer.length === 0) return;

    const entriesToFlush = [...this.logBuffer];
    this.logBuffer = [];

    // Process buffered entries
    entriesToFlush.forEach(entry => {
      this.config.transports.forEach(transport => {
        if (entry.level >= transport.minLevel) {
          try {
            transport.log(entry);
          } catch (error) {
            this.addToRetryQueue(entry);
          }
        }
      });
    });
  }

  private addToRetryQueue(entry: LogEntry): void {
    this.retryQueue.push(entry);
    // Implement retry logic here if needed
  }

  setUserId(userId: string): void {
    // This would be called after user authentication
    this.sessionId = `${this.sessionId}_user_${userId}`;
  }

  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  }
}
